import { Reference } from '@apollo/client';

import useMutationWithValidation from '@hooks/useMutationWithValidation';
import {
  ChatFragment,
  FileResourceFragmentDoc,
  GetChatMessagesDocument,
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
  MessageCreateDocument,
  MessageCreateMutation,
  MessageCreateMutationVariables
} from '@typing/Generated';

type Args = {
  chat: ChatFragment;
  journeyId: string;
  onMessageCreate: () => void;
};

const useCreateMessage = ({ chat, journeyId, onMessageCreate }: Args) => {
  const [createMessage] = useMutationWithValidation<MessageCreateMutation, MessageCreateMutationVariables>(
    MessageCreateDocument,
    'message',
    {
      update: (store, { data }) => {
        if (data?.messageCreate?.message) {
          try {
            const message = data.messageCreate.message;
            const cacheData = store.readQuery<GetChatMessagesQuery, GetChatMessagesQueryVariables>({
              query: GetChatMessagesDocument,
              variables: { id: chat.id }
            });
            if (cacheData) {
              const newEdge = {
                __typename: 'MessageEdge' as const,
                node: message
              };
              const edges = cacheData.chatMessages.edges ?? [];
              const newEdges = [...edges, newEdge];
              const newCacheData = {
                ...cacheData,
                chatMessages: {
                  ...cacheData.chatMessages,
                  edges: newEdges,
                  totalCount: cacheData.chatMessages.totalCount + 1
                }
              };
              store.writeQuery<GetChatMessagesQuery, GetChatMessagesQueryVariables>({
                data: newCacheData,
                query: GetChatMessagesDocument,
                variables: {
                  id: chat.id
                }
              });

              onMessageCreate();
            }
          } catch {
            // Trying to run the query can fail
          }
          data.messageCreate.message.fileResources.forEach(fileResource => {
            store.modify({
              fields: {
                fileResources(cachedFileResourceRefs: readonly Reference[]) {
                  const newFileResourceRef = store.writeFragment({
                    data: fileResource,
                    fragment: FileResourceFragmentDoc,
                    fragmentName: 'FileResource'
                  });
                  if (newFileResourceRef && data.messageCreate?.message) {
                    return [...cachedFileResourceRefs, newFileResourceRef];
                  }
                  return [...cachedFileResourceRefs];
                }
              },
              id: store.identify({ __typename: 'Journey', id: journeyId })
            });
          });
        }
      }
    }
  );
  return createMessage;
};

export default useCreateMessage;
